import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
  FC,
  ReactNode,
} from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  TextField,
  Typography,
  Checkbox,
  FormControlLabel,
  Tooltip,
  InputAdornment,
  Popper,
  Snackbar,
  Alert,
  Paper,
  List,
  ListItem,
  DialogContentText,
  CircularProgress,
  FormGroup,
  ListItemText,
  Chip,
  ListItemButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SearchIcon from "@mui/icons-material/Search";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { styled } from "@mui/material/styles";
import {
  deleteFaq,
  updateFaq,
  fetchPrograms as apiFetchPrograms,
} from "../api/faqApi";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import {
  ProgramOption,
  UpdateFaqPayload,
  UpdateFaqProgram,
} from "../api/types";

interface EditFaqDialogProps {
  id: string;
  open: boolean;
  initialQuestion: string;
  initialAnswer: string;
  initialGlobalVisibility: boolean;
  initialSelectedPrograms?: ProgramOption[];
  onClose: () => void;
  onSuccess: () => void;
  onDeleteSuccess: () => void;
}

const icon = <CheckBoxOutlineBlankIcon sx={{ fontSize: 20 }} />;
const checkedIcon = <CheckBoxIcon sx={{ fontSize: 20 }} />;

const CustomPopper = (props: any) => (
  <Popper
    {...props}
    style={{
      ...props.style,
      maxHeight: 230,
      overflowY: "auto",
      boxShadow: "0px 4px 16px rgba(0,0,0,0.12)",
      borderRadius: 8,
      background: "#fff",
    }}
    placement="bottom-start"
  />
);

const ActionBar = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "flex-end",
  gap: theme.spacing(1.5),
  padding: theme.spacing(2, 3, 2, 3),
  borderTop: "1px solid #eee",
  background: "#fff",
}));

const useDebounce = (callback: Function, delay: number) => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const debouncedCallback = useCallback(
    async (...args: any[]) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(async () => {
        await callback(...args);
      }, delay);
    },
    [callback, delay]
  );

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return debouncedCallback;
};

const DeleteConfirmationDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
}> = ({ open, onClose, onConfirm }) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderRadius: "12px",
          width: "500px",
          maxWidth: "90vw",
        },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 700,
          fontSize: "24px",
          p: 3,
          color: "#000",
          bgcolor: "#fff",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "1px solid #ddd",
        }}
      >
        Delete FAQ
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pt: 4, pb: 4, mt: 4 }}>
        <DialogContentText
          sx={{
            color: "#000",
            fontSize: "18px",
            textAlign: "left",
            fontWeight: 400,
          }}
        >
          Are you sure you want to delete this FAQ?
        </DialogContentText>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 0, justifyContent: "flex-end" }}>
        <Button
          onClick={onClose}
          sx={{
            border: "1px solid #000",
            bgcolor: "#fff",
            color: "#000",
            fontWeight: 600,
            fontSize: "16px",
            textTransform: "none",
            px: 4,
            py: 1,
            borderRadius: "8px",
            marginRight: 1,
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          sx={{
            bgcolor: "#000",
            color: "#fff",
            fontWeight: 600,
            fontSize: "16px",
            textTransform: "none",
            px: 4,
            py: 1,
            borderRadius: "8px",
            "&:hover": {
              bgcolor: "#333",
            },
          }}
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const EditFaqDialog: FC<EditFaqDialogProps> = ({
  id,
  open,
  initialQuestion,
  initialAnswer,
  initialGlobalVisibility,
  initialSelectedPrograms = [],
  onClose,
  onSuccess,
  onDeleteSuccess,
}) => {
  const [question, setQuestion] = useState(initialQuestion);
  const [answer, setAnswer] = useState(initialAnswer);
  const [globalVisibility, setGlobalVisibility] = useState(
    initialGlobalVisibility
  );

  const [selectedPrograms, setSelectedPrograms] = useState<ProgramOption[]>([]);
  const [tempSelectedPrograms, setTempSelectedPrograms] = useState<
    ProgramOption[]
  >([]);
  const [inputValue, setInputValue] = useState<string>("");
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [programs, setPrograms] = useState<ProgramOption[]>([]);
  const [programFetchError, setProgramFetchError] = useState<string | null>(
    null
  );
  const anchorEl = useRef<HTMLDivElement>(null);

  const [isSaving, setIsSaving] = useState(false);
  const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] = useState(false);
  const [errors, setErrors] = useState({
    question: false,
    answer: false,
    programs: false,
  });
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "info" | "warning";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const validateFields = (): boolean => {
    const newErrors = {
      question: !question.trim(),
      answer: !answer.trim(),
      programs:
        !globalVisibility &&
        (!selectedPrograms || selectedPrograms.length === 0),
    };
    setErrors(newErrors);
    const programError = !globalVisibility && selectedPrograms.length === 0;
    if (programError) {
      setSnackbar({
        open: true,
        message:
          "Please select at least one program or enable Global Visibility.",
        severity: "error",
      });
    }
    return (
      !newErrors.question &&
      !newErrors.answer &&
      !newErrors.programs &&
      !programError
    );
  };

  const handleClose = () => {
    if (isSaving) return;
    setIsOpen(false);
    setPrograms([]);
    setProgramFetchError(null);
    setInputValue("");
    setErrors({ question: false, answer: false, programs: false });
    onClose();
  };

  const handleSave = async () => {
    setErrors({
      question: false,
      answer: false,
      programs: false,
    });

    const newErrors = {
      question: !question.trim(),
      answer: !answer.trim(),
      programs: !globalVisibility && selectedPrograms.length === 0,
    };

    setErrors(newErrors);

    if (Object.values(newErrors).some((error) => error)) {
      return;
    }

    try {
      const userDetailsStr = localStorage.getItem("maiaskUser") || "{}";
      let userDetails;
      try {
        userDetails = JSON.parse(userDetailsStr);
      } catch (e) {
        console.error("Error parsing user details:", e);
        userDetails = {};
      }

      const payload = {
        faq_id: id,
        question: question.trim(),
        answer: answer.trim(),
        is_global: globalVisibility,
        programs: selectedPrograms.map((program) => ({
          name: program.label,
          code: program.code!,
          pok: program.pok || null,
        })),
        email: userDetails.email || "",
      };

      const response = await updateFaq(payload);
      if (response.output?.status) {
        setSnackbar({
          open: true,
          message: "FAQ updated successfully",
          severity: "success",
        });
        onSuccess();
        handleClose();
      } else {
        throw new Error(response.output?.message || "Failed to update FAQ");
      }
    } catch (error) {
      console.error("Error updating FAQ:", error);
      setSnackbar({
        open: true,
        message:
          error instanceof Error
            ? error.message
            : "Failed to update FAQ. Please try again.",
        severity: "error",
      });
    }
  };

  const handleDeleteClick = () => {
    setConfirmDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    setConfirmDeleteDialogOpen(false);
    setIsSaving(true);
    try {
      const response = await deleteFaq(id);
      if (response.output?.status) {
        setSnackbar({
          open: true,
          message: "FAQ deleted successfully!",
          severity: "success",
        });
        onDeleteSuccess();
        handleClose();
      } else {
        throw new Error(response.output?.message || "Failed to delete FAQ");
      }
    } catch (error) {
      console.error("Failed to delete FAQ:", error);
      setSnackbar({
        open: true,
        message:
          error instanceof Error ? error.message : "Failed to delete FAQ.",
        severity: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setInputValue(value);
  };

  const formatInputValue = (selected: ProgramOption[]): string => {
    if (selected.length === 0) return "";
    return `${selected.length} selected`;
  };

  const handleSelectAll = () => {
    setTempSelectedPrograms(programs);
  };

  const handleClearAll = () => {
    setTempSelectedPrograms([]);
  };

  const handleDone = () => {
    setSelectedPrograms(tempSelectedPrograms);
    setInputValue(formatInputValue(tempSelectedPrograms));
    setIsOpen(false);
  };

  const handleToggleDropdown = () => {
    if (!isOpen) {
      setTempSelectedPrograms(selectedPrograms);
      if (
        selectedPrograms.length > 0 &&
        inputValue === `${selectedPrograms.length} selected`
      ) {
        setInputValue("");
      }
      if (programs.length === 0 && !loading) {
        debouncedFetchPrograms(inputValue);
      }
    } else {
      handleDone();
    }
    setIsOpen(!isOpen);
  };

  const handleProgramSelect = (program: ProgramOption) => {
    setTempSelectedPrograms((prevSelectedPrograms) => {
      const isSelected = prevSelectedPrograms.some(
        (p) => p.value === program.value
      );
      if (isSelected) {
        return prevSelectedPrograms.filter((p) => p.value !== program.value);
      } else {
        return [...prevSelectedPrograms, program];
      }
    });
  };

  const debouncedFetchPrograms = useDebounce(async (currentQuery: string) => {
    setLoading(true);
    setProgramFetchError(null);
    setPrograms([]);
    try {
      const fetchedPrograms = await apiFetchPrograms(currentQuery);
      setPrograms(fetchedPrograms);
    } catch (error) {
      console.error("Failed to fetch programs:", error);
      setProgramFetchError("Failed to load programs.");
      setPrograms([]);
    } finally {
      setLoading(false);
    }
  }, 500);

  useEffect(() => {
    if (isOpen && !globalVisibility) {
      debouncedFetchPrograms(inputValue);
    }
  }, [isOpen, inputValue, globalVisibility]);

  useEffect(() => {
    if (open) {
      console.log(initialSelectedPrograms);
      setQuestion(initialQuestion);
      setAnswer(initialAnswer);
      setGlobalVisibility(initialGlobalVisibility);
      setSelectedPrograms(initialSelectedPrograms);
      setTempSelectedPrograms(initialSelectedPrograms);
      setInputValue(formatInputValue(initialSelectedPrograms));
      setErrors({ question: false, answer: false, programs: false });
      setSnackbar({ open: false, message: "", severity: "success" });
      setIsSaving(false);
      setIsOpen(false);
      setPrograms([]);
      setProgramFetchError(null);
    }
  }, [
    open,
    initialQuestion,
    initialAnswer,
    initialGlobalVisibility,
    initialSelectedPrograms,
  ]);

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            borderRadius: "6px !important",
            width: "80vw",
            height: "85vh",
            maxWidth: "80vw",
            maxHeight: "85vh",
            p: 0,
            overflow: "visible",
            display: "flex",
            flexDirection: "column",
            bgcolor: "#fff",
            "& .MuiDialog-paper": {
              borderRadius: "6px !important",
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: "#f7f7f7",
            fontWeight: 700,
            fontSize: 20,
            pb: 2,
            pt: 3,
            px: 3,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid #eee",
            m: 0,
            borderRadius: "6px",
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, color: "#111" }}>
            Edit FAQ
          </Typography>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            disabled={isSaving}
            sx={{ ml: 2 }}
          >
            <CloseIcon sx={{ fontSize: 24 }} />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            px: 3,
            py: 3,
            overflowY: "auto",
            flexGrow: 1,
            bgcolor: "#fff",
            "&.MuiDialogContent-root": {
              padding: "24px",
            },
          }}
        >
          <Box>
            <Typography variant="subtitle2" sx={{ my: 1, color: "#000" }}>
              Question <span style={{ color: "red" }}>*</span>
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Enter the question"
              value={question}
              onChange={(e) => {
                setQuestion(e.target.value);
                if (errors.question) setErrors({ ...errors, question: false });
              }}
              error={errors.question}
              helperText={errors.question ? "Question cannot be empty." : " "}
              disabled={isSaving}
              sx={{ bgcolor: "#fff" }}
              InputProps={{ sx: { fontSize: "14px" } }}
              FormHelperTextProps={{ sx: { mx: 0 } }}
            />
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 0.5, color: "#000" }}>
              Answer <span style={{ color: "red" }}>*</span>
            </Typography>
            <TextField
              fullWidth
              maxRows={20}
              minRows={1}
              variant="outlined"
              placeholder="Provide the answer"
              value={answer}
              onChange={(e) => {
                setAnswer(e.target.value);
                if (errors.answer) setErrors({ ...errors, answer: false });
              }}
              error={errors.answer}
              helperText={errors.answer ? "Answer cannot be empty." : " "}
              disabled={isSaving}
              sx={{ bgcolor: "#fff" }}
              InputProps={{ sx: { fontSize: "14px" } }}
              FormHelperTextProps={{ sx: { mx: 0 } }}
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mb: 2.5,
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={globalVisibility}
                  onChange={(e) => {
                    setGlobalVisibility(e.target.checked);
                    if (e.target.checked) {
                      setSelectedPrograms([]);
                      setTempSelectedPrograms([]);
                      setInputValue("");
                    }
                  }}
                  disabled={isSaving}
                  sx={{ py: 0.5 }}
                />
              }
              label={
                <Typography variant="body2" sx={{ color: "#000" }}>
                  Global Visibility
                </Typography>
              }
              sx={{ ml: -1.2, mr: 1 }}
            />

            <Tooltip
              title={
                <Typography sx={{ fontSize: 12, lineHeight: 1.4 }}>
                  Selecting global visibility will display these FAQs across all
                  programs.
                  <br />
                  Once set to global, program associations cannot be edited or
                  removed.
                </Typography>
              }
              arrow
              placement="left"
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: "#000",
                    color: "#fff",
                    px: 2,
                    py: 1.2,
                    borderRadius: "4px",
                    maxWidth: 300,
                    fontSize: "12px",
                  },
                },
                arrow: {
                  sx: {
                    color: "#000",
                  },
                },
              }}
            >
              <InfoOutlinedIcon
                sx={{ color: "#000", fontSize: 18, cursor: "pointer", mt: 0.2 }}
              />
            </Tooltip>
          </Box>

          {!globalVisibility && (
            <Box sx={{ mb: 2.5, position: "relative" }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5, color: "#000" }}>
                List of Programs to which this FAQ is linked
              </Typography>
              <Box ref={anchorEl} onClick={handleToggleDropdown}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder={
                    selectedPrograms.length > 0
                      ? formatInputValue(selectedPrograms)
                      : "Search and select programs..."
                  }
                  value={inputValue}
                  onChange={handleInputChange}
                  disabled={isSaving || globalVisibility}
                  sx={{ bgcolor: "#fff" }}
                  InputProps={{
                    sx: { fontSize: "14px", cursor: "pointer" },
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: "#aaa" }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
              <Popper
                open={isOpen && !globalVisibility}
                anchorEl={anchorEl.current}
                placement="bottom-start"
                style={{ zIndex: 1301, width: anchorEl.current?.clientWidth }}
              >
                <Paper
                  elevation={3}
                  sx={{
                    maxHeight: 220,
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: "8px",
                    overflow: "hidden",
                  }}
                >
                  <List
                    sx={{
                      overflowY: "auto",
                      maxHeight: 250,
                      flexGrow: 1,
                      p: 0,
                    }}
                  >
                    {!loading && programFetchError && (
                      <ListItem
                        sx={{ justifyContent: "center", color: "error.main" }}
                      >
                        {programFetchError}
                      </ListItem>
                    )}
                    {!loading &&
                      !programFetchError &&
                      programs.length === 0 &&
                      inputValue && (
                        <ListItem
                          sx={{
                            justifyContent: "center",
                            color: "text.secondary",
                          }}
                        >
                          No programs match '{inputValue}'
                        </ListItem>
                      )}
                    {!loading &&
                      !programFetchError &&
                      programs.length === 0 &&
                      !inputValue && (
                        <ListItem
                          sx={{
                            justifyContent: "center",
                            color: "text.secondary",
                          }}
                        >
                          Type to search or clear search to see all programs.
                        </ListItem>
                      )}

                    {!loading &&
                      programs.map((program) => (
                        <ListItem
                          key={program.value}
                          dense
                          onClick={() => handleProgramSelect(program)}
                          sx={{
                            py: 0.5,
                            px: 1.5,
                            cursor: "pointer",
                            "&:hover": { bgcolor: "action.hover" },
                          }}
                        >
                          <Checkbox
                            icon={icon}
                            checkedIcon={checkedIcon}
                            checked={tempSelectedPrograms.some(
                              (p) => p.value === program.value
                            )}
                            size="small"
                            sx={{ mr: 1, p: 0.5 }}
                          />
                          <ListItemText primary={program.label} />
                        </ListItem>
                      ))}
                  </List>
                  {!loading && !programFetchError && programs.length > 0 && (
                    <ActionBar
                      sx={{
                        borderTop: "1px solid #eee",
                        background: "#f8f9fa",
                      }}
                    >
                      <Button
                        onClick={handleSelectAll}
                        sx={{
                          textTransform: "none",
                          fontWeight: 600,
                          color: "#000",
                          fontSize: 14,
                        }}
                      >
                        Select All ({programs.length})
                      </Button>
                      <Button
                        onClick={handleClearAll}
                        sx={{
                          fontSize: 14,
                          textTransform: "none",
                          fontWeight: 600,
                          color: "#000",
                          border: "1px solid #000",
                          borderRadius: "4px",
                          px: 2,
                        }}
                      >
                        Clear All ({tempSelectedPrograms.length})
                      </Button>
                      <Button
                        onClick={handleDone}
                        sx={{
                          textTransform: "none",
                          fontWeight: 600,
                          fontSize: 14,
                          bgcolor: "#000",
                          color: "#fff",
                          px: 2,
                        }}
                      >
                        Done
                      </Button>
                    </ActionBar>
                  )}
                </Paper>
              </Popper>
              {!globalVisibility && selectedPrograms.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography
                    variant="caption"
                    sx={{ color: "#555", fontWeight: 500 }}
                  >
                    Selected Programs:
                  </Typography>
                  <List
                    dense
                    sx={{
                      maxHeight: 100,
                      overflowY: "auto",
                      bgcolor: "#fff",
                      borderRadius: "4px",
                      border: "1px solid #eee",
                      p: 2,
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.5,
                    }}
                  >
                    {selectedPrograms.map((p) => (
                      <ListItem
                        key={p.value}
                        dense
                        sx={{
                          py: 0.2,
                          px: 1,
                        }}
                      >
                        <Typography variant="body2" sx={{ fontSize: "14px" }}>
                          {p.label}
                        </Typography>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: "1px solid #eee",
            bgcolor: "#fff",
            justifyContent: "flex-end",
            gap: 2,
            flexShrink: 0,
            borderRadius: "6px",
          }}
        >
          <Button
            onClick={handleClose}
            variant="outlined"
            disabled={isSaving}
            sx={{
              borderRadius: "6px",
              minWidth: 100,
              fontSize: 14,
              fontWeight: 600,
              bgcolor: "#fff",
              color: "#000",
              borderColor: "#000",
              ":hover": { bgcolor: "#f7f7f7", borderColor: "#000" },
              padding: "16px 30px",
              height: "42px",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={
              isSaving ||
              (!globalVisibility && selectedPrograms.length === 0) ||
              !question.trim() ||
              !answer.trim()
            }
            sx={{
              borderRadius: "6px",
              minWidth: 100,
              fontSize: 14,
              fontWeight: 600,
              bgcolor: "#000",
              color: "#fff",
              boxShadow: "none",
              ":hover": { bgcolor: "#222" },
              padding: "16px 30px",
              height: "42px",
              "&.Mui-disabled": { bgcolor: "#b0bec5", color: "#607d8b" },
            }}
          >
            {isSaving ? "Confirming ..." : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>

      <DeleteConfirmationDialog
        open={confirmDeleteDialogOpen}
        onClose={() => setConfirmDeleteDialogOpen(false)}
        onConfirm={handleConfirmDelete}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default EditFaqDialog;
